'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "format", "interval", "menuWidth", "size", "use", "value"];
import classNames from 'classnames';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { Component } from 'react';
import devLogger from 'react-utils/devLogger';
import Controllable from '../decorators/Controllable';
import UISelect from '../input/UISelect';
import virtualizedMenuRenderer from '../input/utils/virtualizedMenuRenderer';
import UITypeaheadResultsItem from '../typeahead/results/UITypeaheadResultsItem';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import deprecated from '../utils/propTypes/deprecated';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { jsx as _jsx } from "react/jsx-runtime";
const MINUTES_IN_A_DAY = 24 * 60;
const NUMBER_REGEX = /\d/;
const condense = str => str.replace(/\s/g, '').toLowerCase();
const formatTime = (format, value) => I18n.moment.utc(0).minutes(value).format(format);
const generateOptions = (extraTimes, format, interval, max, min, normalizedValue) => {
  if (interval <= 0) {
    const msg = 'UITimePicker: Interval cannot be a number less than or equal to 0';
    devLogger.warn({
      message: msg,
      key: msg
    });
  }
  const calculatedInterval = interval <= 0 ? 1 : interval;
  const options = [];
  const computedMin = typeof min === 'number' ? min : 0;
  const computedMax = typeof max === 'number' ? max : MINUTES_IN_A_DAY;
  for (let value = 0; value < MINUTES_IN_A_DAY; value += calculatedInterval) {
    const outOfRange = computedMin > value || value > computedMax;
    if (outOfRange) {
      continue;
    }
    const text = formatTime(format, value);
    options.push({
      text,
      value
    });
    if (normalizedValue && normalizedValue > value && normalizedValue < value + calculatedInterval) {
      const normalizedValueText = I18n.moment.utc(0).minutes(normalizedValue).format(format);
      options.push({
        text: normalizedValueText,
        value: normalizedValue
      });
    }
  }
  extraTimes.forEach(value => {
    if (!options.some(option => option.value === value)) {
      const text = formatTime(format, value);
      options.push({
        text,
        value
      });
    }
  });
  return options;
};
const normalizeValue = (format, value) => {
  // Return a string corresponding to the number of minutes into the day
  // represented by the value.
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  devLogger.warn({
    message: `UITimePicker: String value "${value}" given. This can cause inconsistent behavior.`,
    key: 'UITimePicker: string value'
  });
  const moment = I18n.moment.utc(value, format);
  return moment.hours() * 60 + moment.minutes();
};
const optionMatchesSearch = (option, search) => {
  if (!search) {
    return true;
  }
  // Ignore whitespace and casing
  const condensedSearch = condense(search);
  const condensedOptionText = condense(option.text);

  // Match front-to-back, allowing chars to be skipped (except the first
  // number). So e.g. a search for "8" matches all times of the form "8:xx",
  // and a search for "815" matches "8:15 AM" and "8:15 PM".
  const searchRegexMatch = condensedSearch.match(NUMBER_REGEX);
  if (searchRegexMatch && searchRegexMatch[0] !== option.text.match(NUMBER_REGEX)[0]) {
    return false;
  }
  let lastSearchCharMatchPos = -1;
  for (let i = 0; i < condensedSearch.length; i++) {
    const searchCharMatchPos = condensedOptionText.indexOf(condensedSearch[i], lastSearchCharMatchPos + 1);
    if (searchCharMatchPos === -1) {
      return false;
    }
    lastSearchCharMatchPos = searchCharMatchPos;
  }
  return true;
};
const getSearchResults = (options, search) => {
  const filteredOptions = options.filter(option => optionMatchesSearch(option, search));
  const exactMatches = [];
  const otherMatches = [];
  filteredOptions.forEach(option => {
    if (condense(option.text).startsWith(condense(search))) {
      exactMatches.push(option);
    } else {
      otherMatches.push(option);
    }
  });
  return [...exactMatches, ...otherMatches];
};
const deprecatedMinMaxMessage = propName => {
  const newPropName = propName.replace('Value', '');
  return {
    message: `UITimePicker: The \`${propName}\` prop is deprecated. Use \`${newPropName}\` instead.`,
    key: 'UITimePicker-old-minmax'
  };
};
const valueRenderer = option => /*#__PURE__*/_jsx(UITypeaheadResultsItem, {
  "aria-invalid": option.invalid,
  itemComponent: "span",
  option: Object.assign({}, option, {
    icon: 'time'
  }),
  _useButton: false,
  _useDropdownText: false
});
const getButtonSize = (shorthandSize, use) => {
  if (shorthandSize === 'sm' && use === 'on-dark') {
    return 'sm';
  }
  return 'default';
};
const getButtonUse = (shorthandSize, use) => {
  if (use === 'on-dark') {
    return shorthandSize === 'sm' ? 'tertiary-extra-light' : 'form-on-dark';
  }
  return shorthandSize === 'sm' ? 'link' : 'form';
};
const getMenuWidth = (menuWidth, shorthandSize) => {
  if (menuWidth) {
    return menuWidth;
  }
  if (shorthandSize === 'sm') {
    return 300;
  }
  return undefined;
};
class UITimePicker extends Component {
  static getDerivedStateFromProps(props, state) {
    const {
      extraTimes,
      format,
      interval,
      max,
      maxValue,
      min,
      minValue,
      value
    } = props;
    if (extraTimes !== state.prevExtraTimes || format !== state.prevFormat || interval !== state.prevInterval || max !== state.prevMax || maxValue !== state.prevMaxValue || min !== state.prevMin || minValue !== state.prevMinValue || value !== state.prevValue) {
      const normalizedValue = normalizeValue(format, value);
      return {
        options: generateOptions(extraTimes, format, interval, max || maxValue, min || minValue, normalizedValue)
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.focus = () => {
      if (this._timeInputRef) {
        this._timeInputRef.focus();
      }
    };
    const {
      extraTimes,
      format,
      interval,
      max,
      maxValue,
      min,
      minValue,
      value
    } = props;
    const normalizedValue = normalizeValue(format, value);
    this.state = {
      options: generateOptions(extraTimes, format, interval, max || maxValue, min || minValue, normalizedValue),
      prevExtraTimes: props.extraTimes,
      prevFormat: props.format,
      prevInterval: props.interval,
      prevMax: props.max,
      prevMaxValue: props.maxValue,
      prevMin: props.min,
      prevMinValue: props.minValue,
      prevValue: props.value
    };
  }
  render() {
    const _this$props = this.props,
      {
        className,
        format,
        menuWidth,
        size,
        use,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const shorthandSize = toShorthandSize(size);
    const buttonSize = getButtonSize(shorthandSize, use);
    const buttonUse = getButtonUse(shorthandSize, use);
    const computedMenuWidth = getMenuWidth(menuWidth, shorthandSize);
    return /*#__PURE__*/_jsx(UISelect, Object.assign({}, rest, {
      buttonSize: buttonSize,
      buttonUse: buttonUse,
      className: classNames('private-timepicker', className),
      filterOptions: getSearchResults,
      matchPos: "start",
      matchProp: "label",
      menuRenderer: virtualizedMenuRenderer,
      menuWidth: computedMenuWidth,
      options: this.state.options,
      ref: elt => {
        this._timeInputRef = elt;
      },
      resetValue: null,
      value: normalizeValue(format, value),
      valueRenderer: valueRenderer
    }));
  }
}
UITimePicker.displayName = 'UITimePicker';
UITimePicker.defaultProps = {
  clearable: true,
  extraTimes: [],
  format: 'LT',
  interval: 30,
  placeholder: () => I18n.text('salesUI.UITimePicker.placeholder'),
  size: 'default',
  use: 'default'
};
UITimePicker.propTypes = {
  clearable: PropTypes.bool.isRequired,
  extraTimes: PropTypes.arrayOf(PropTypes.number).isRequired,
  format: PropTypes.string.isRequired,
  interval: PropTypes.number.isRequired,
  max: PropTypes.number,
  maxValue: deprecated(PropTypes.number, deprecatedMinMaxMessage),
  menuWidth: UISelect.propTypes.menuWidth,
  min: PropTypes.number,
  minValue: deprecated(PropTypes.number, deprecatedMinMaxMessage),
  onChange: PropTypes.func,
  placeholder: createLazyPropType(PropTypes.node).isRequired,
  size: PropTypes.oneOfType([propTypeForSizes(['sm']), PropTypes.oneOf(['default'])]).isRequired,
  use: PropTypes.oneOf(['default', 'on-dark']).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  error: PropTypes.bool
};
export default Controllable(UITimePicker);